import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Button, Form, Spinner } from "react-bootstrap";
import payrollData from "../../../store/jsondata/payrollData.json";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import usePayrollApi from "../../../services/payroll/payrollApi";
import useHrApi from "../../../services/hr/hrAPI";
import { toast } from "react-toastify";
import { GetCurrentUserName } from "../../../utils/permissionChecker";
import moment from "moment";
const AddBenefit = ({ closeModal, initialValues }) => {
  const { t } = useTranslation();
  const loggedInUser = GetCurrentUserName();
  const { addBenefit, updateBenefit } = usePayrollApi(); // Assuming you have an updateBenefit function
  const { getAllBranch, getAllGrade } = useHrApi();
  const queryClient = useQueryClient();
  const mutationFunction = initialValues ? updateBenefit : addBenefit; // Determine whether to add or update
  const { mutate: BenefitMutation } = useMutation(mutationFunction);
  const getGradeQuery = useQuery(["get_benefit_grade"], () => getAllGrade());
  const getBranchQuery = useQuery(["get_benefit_branch"], () => getAllBranch());
  if (getGradeQuery.isLoading || getBranchQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (getGradeQuery.isError || getBranchQuery.isError) {
    return <div>Error: Unable to fetch data</div>;
  }
  const grade = getGradeQuery.data;
  const branch = getBranchQuery.data;
  const validationSchema = Yup.object().shape({
    from_date: Yup.string().trim().required("Applicable date is required"),
    to_date: Yup.string()
      .trim()
      .required("End date is required")
      .test("end-date", "End date must be after start date", function (value) {
        const { from_date } = this.parent;
        if (!value || !from_date) return true;
        return new Date(value) > new Date(from_date);
      }),
    benefit_name: Yup.string().trim().required("Required"),
    benefit_description: Yup.string().required("Required"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required")
      .positive("Amount must be a positive number"),
    branch_id: Yup.string().required("Required"),
    grade_id: Yup.string().required("Required"),
    is_taxable: Yup.string().required("Required"),
    is_percentage: Yup.string().required("Required"),
  });

  const onSubmit = async (values) => {
    try {
      BenefitMutation(values, {
        onSuccess: () => {
          closeModal();
          queryClient.invalidateQueries("benefit_list");
          toast.success(
            initialValues
              ? "Benefit updated successfully"
              : "New benefit registered successfully"
          );
        },
        onError: (error) => {
          toast.error(
            initialValues
              ? `Failed to update benefit: ${error?.message}`
              : `Failed to register new benefit: ${error?.message}`
          );
        },
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <Formik
            initialValues={
              initialValues || {
                from_date: moment(new Date()).format("YYYY-MM-DD"),
                to_date: moment(new Date()).format("YYYY-MM-DD"),
                amount: "",
                is_taxable: "",
                status: 1,
                is_percentage: "",
                grade_id: "",
                branch_id: "",
                benefit_name: "",
                benefit_description: "",
                created_by: loggedInUser,
                updated_by: loggedInUser,
              }
            }
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps) => (
              <Form noValidate onSubmit={formikProps.handleSubmit}>
                <Form.Group controlId="benefit_name">
                  <Form.Label>{t("benefit_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="benefit_name"
                    value={formikProps.values.benefit_name}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.benefit_name &&
                      !!formikProps.errors.benefit_name
                    }
                    placeholder={t("benefit_name")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.benefit_name}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="text-secondary">
                    {t("benefit_description")}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="benefit_description"
                    value={formikProps.values.benefit_description}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.benefit_description &&
                      !!formikProps.errors.benefit_description
                    }
                    placeholder={t("benefit_description")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.benefit_description}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="branch_id">
                  <Form.Label>{t("branch")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="branch_id"
                    value={formikProps.values.branch_id}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.branch_id &&
                      !!formikProps.errors.branch_id
                    }
                  >
                    <option value="" disabled>
                      {t("select_branch")}
                    </option>
                    {branch &&
                      branch.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.branch_id}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="grade_id">
                  <Form.Label>{t("grade")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="grade_id"
                    value={formikProps.values.grade_id}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.grade_id &&
                      !!formikProps.errors.grade_id
                    }
                  >
                    <option value="" disabled>
                      {t("select_grade")}
                    </option>
                    {grade &&
                      grade.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.grade_id}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="amount">
                  <Form.Label>{t("amount")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="amount"
                    value={formikProps.values.amount}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.amount && !!formikProps.errors.amount
                    }
                    placeholder={t("amount")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.amount}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="is_percentage">
                  <Form.Label>{t("benefit_type")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="is_percentage"
                    value={formikProps.values.is_percentage}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.is_percentage &&
                      !!formikProps.errors.is_percentage
                    }
                  >
                    <option value="" disabled>
                      {t("benefit_type")}
                    </option>
                    {payrollData.benefit_type.map((data) => (
                      <option
                        key={data.is_percentage}
                        value={data.is_percentage}
                      >
                        {t(data.name)}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.is_percentage}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="is_taxable">
                  <Form.Label>{t("is_taxable")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="is_taxable"
                    value={formikProps.values.is_taxable}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.is_taxable &&
                      !!formikProps.errors.is_taxable
                    }
                  >
                    <option value="" disabled>
                      {t("is_taxable")}
                    </option>
                    {payrollData.is_taxable.map((data) => (
                      <option key={data.is_taxable} value={data.is_taxable}>
                        {t(data.name)}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.is_taxable}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="from_date">
                  <Form.Label>{t("Starting Date")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="from_date"
                    value={moment(
                      new Date(formikProps.values.from_date)
                    ).format("YYYY-MM-DD")}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.from_date &&
                      !!formikProps.errors.from_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.from_date}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="to_date">
                  <Form.Label>{t("End Date")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="to_date"
                    value={moment(new Date(formikProps.values.to_date)).format(
                      "YYYY-MM-DD"
                    )}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.to_date &&
                      !!formikProps.errors.to_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.to_date}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  className="mr-2"
                  variant="secondary"
                  onClick={closeModal}
                >
                  {t("close")}
                </Button>
                <Button
                  className=" btn btn-info mr-1"
                  variant="primary"
                  type="submit"
                  disabled={formikProps.isSubmitting} // Disable the button while submitting
                >
                  {formikProps.isSubmitting ? (
                    <span>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="false"
                      />
                      <span className="visually-hidden">{t("Sending...")}</span>
                    </span>
                  ) : initialValues ? (
                    t("update")
                  ) : (
                    t("register")
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default AddBenefit;
